import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { IoIosArrowDown } from 'react-icons/io'

import { USER_MEMBERSHIP_GROUPS } from '../../../../../const/membership-const'
import { MESSAGES_CONST } from '../../../../../const/messages-const'
import useToasterHelper from '../../../../../helpers/ToasterHelper'
import useProfileHook from '../../../../../hooks/users/competitor/profile/useProfileHook'
import { getConvertedData } from '../../../../../models/interface.helper'
import {
  IUserInterface,
  NumberMembershipGroupKey,
} from '../../../../../models/users/user.interface'
import { UserModel } from '../../../../../models/users/user.model'
import UserService from '../../../../../services/userService'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectUserId, setProfileDetails } from '../../../../../store/user/userSlice'
import { IconSearch } from '../../../../icons/IconSearch'
import FormHeader from '../../../../ui/form/form-header/FormHeader'
import { CompetitorNumberCard } from '../CompetitorNumberCard'
import { EmptyCards } from '../OrganizerMembershipsTab/components/EmptyCards/EmptyCards'
import { PaymentsItems } from '../OrganizerMembershipsTab/components/PaymentsItems/PaymentsItems'

export const CompetitorMembershipsTab = () => {
  const dispatch = useAppDispatch()
  const userId = useAppSelector(selectUserId)
  const toastFunctions = useToasterHelper()
  const [loading, setLoading] = useState(false)
  const [userData, setUserData] = useState<IUserInterface | null>(null)
  const [saved, setSaved] = useState(true)
  const [activeTab, setActiveTab] = useState<NumberMembershipGroupKey>('English')
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const { updateUserDetails, getModelledUserDetails } = useProfileHook({})

  const { reset, watch, control, register, setValue, handleSubmit } = useForm<IUserInterface>({
    mode: 'onChange',
  })

  const { append, fields, remove, update } = useFieldArray({
    control,
    name: 'userExtraDocument',
  })

  // Local var for user membership group const data
  const membershipGroups = USER_MEMBERSHIP_GROUPS

  useEffect(() => {
    const getUserData = async () => {
      let user_doc = await UserService.getUserInfoById(userId)
      if (user_doc.exists()) {
        const user = UserModel.fromFirestoreDoc(user_doc).toObject()
        reset({ ...user })
        setUserData(user)
      }
    }
    getUserData()
  }, [userId, reset])

  useEffect(() => {
    if (!searchValue) {
      setSearchResults([])
      return
    }

    const allowedTabs: NumberMembershipGroupKey[] = Object.keys(membershipGroups).filter(
      (tab): tab is NumberMembershipGroupKey => tab !== 'Pegasus'
    )

    // combine memberships from all tabs except pegasus tab which has no numbers
    const allMemberships = allowedTabs.flatMap((tab) =>
      membershipGroups[tab].map((membership) => ({
        ...membership,
        tab,
        type: 'standard' as const,
        documentNumber: userData?.[membership.fieldKey]?.documentNumber || '',
      }))
    )

    // add custom fields to search results
    const customFields = fields.map((field, index) => ({
      fieldKey: `userExtraDocument.${index}`,
      label: field.documentFullName || 'Custom Membership',
      documentNumber: field.documentNumber || '',
      tab: 'Other' as const,
      type: 'custom' as const,
    }))

    const combinedMemberships = [...allMemberships, ...customFields]

    const searchTermLower = searchValue.toLowerCase()
    // filter based on search value
    const filteredResults = combinedMemberships.filter((membership) => {
      return (
        membership.label.toLowerCase().includes(searchTermLower) ||
        membership.documentNumber.toLowerCase().includes(searchTermLower)
      )
    })

    setSearchResults(filteredResults)
  }, [searchValue, membershipGroups, fields, userData])

  const onSubmit = async (dataForm: IUserInterface) => {
    setLoading(true)
    try {
      // Removed user select to select the membership status of active of expired, instead we will determine if the membership status based on the user entered expiration date and the current date
      // Process each membership object in the form data
      Object.keys(dataForm).forEach((key) => {
        if (dataForm[key] && typeof dataForm[key] === 'object' && dataForm[key].endDate) {
          const expDate = new Date(dataForm[key].endDate)
          const today = new Date()

          // Check if this is a lifetime membership date (Dec 31, 2099)
          const isLifetimeMembership =
            expDate.getFullYear() === 2099 && expDate.getMonth() === 11 && expDate.getDate() === 31

          // Set the membership status based on the date
          if (isLifetimeMembership) {
            dataForm[key].membershipStatus = 'Lifetime'
          } else {
            dataForm[key].membershipStatus = expDate > today ? 'Active' : 'Expired'
          }
        }
      })

      let profileDetails = new UserModel(dataForm).toObject()
      profileDetails = getConvertedData(profileDetails)
      const dataToThrow = getModelledUserDetails({
        ...profileDetails,
      })

      await updateUserDetails(dataToThrow)
      dispatch(setProfileDetails(profileDetails))

      setUserData(profileDetails)
      toastFunctions.success({ message: MESSAGES_CONST.MEMBERSHIP_NUMBER_UPDATED })
      setSaved(true)
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  const isMembershipCompleted = useCallback(
    (fieldKey: string) => {
      if (!userData) return false

      // for custom fields in the userExtraDocument array - "other tab"
      if (fieldKey.startsWith('userExtraDocument.')) {
        const index = parseInt(fieldKey.split('.')[1])
        // check if this index exists in the fields array
        if (fields && fields[index]) {
          const field = fields[index]
          return (
            field && field.documentNumber && field.documentNumber.trim() !== '' && field.endDate
          )
        }
        return false
      }

      // else -> for standard membership fields
      return (
        userData[fieldKey] &&
        userData[fieldKey].documentNumber &&
        userData[fieldKey].documentNumber.trim() !== '' &&
        userData[fieldKey].endDate
      )
    },
    [userData, fields]
  )

  const { completed: completedCustomFields, incomplete: incompleteCustomFields } = useMemo(() => {
    return {
      completed: fields.filter((_, index) => isMembershipCompleted(`userExtraDocument.${index}`)),
      incomplete: fields.filter((_, index) => !isMembershipCompleted(`userExtraDocument.${index}`)),
    }
  }, [fields, isMembershipCompleted])

  const { completed: completedMemberships, incomplete: incompleteMemberships } = useMemo(() => {
    if (!activeTab || activeTab === 'Pegasus') return { completed: [], incomplete: [] }

    const memberships = membershipGroups[activeTab] || []

    return {
      completed: memberships.filter((membership) => isMembershipCompleted(membership.fieldKey)),
      incomplete: memberships.filter((membership) => !isMembershipCompleted(membership.fieldKey)),
    }
  }, [activeTab, membershipGroups, isMembershipCompleted])

  const renderHeaderButtons = () => {
    if (activeTab === 'Pegasus') {
      return null
    }

    return (
      <div className="flex flex-col sm:flex-row  w-full items-center sm:justify-end gap-2 sticky top-0 ">
        <div className=" border relative h-[46px] rounded-lg w-full md:max-w-[449px] border-SeabiscuitSkeletonColor">
          <div className="opacity-50 absolute inset-y-0 left-0 flex z-50 items-center pl-3 pointer-events-none">
            <IconSearch />
          </div>
          <input
            type="search"
            className="rounded-lg block w-full bg-transparent p-0 pl-10 text-sm border-0 h-full text-gray-900 placeholder:text-SeabiscuitDark200ThemeColor"
            placeholder="Search memberships..."
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>
        {saved ? (
          <button
            type="button"
            onClick={() => setSaved(false)}
            className="items-center !sticky top-0 bg-white right-10 w-[150px] h-[45px] border border-SeabiscuitMainThemeColor rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitMainThemeColor saveBtn"
          >
            Edit
          </button>
        ) : (
          <div className="flex-col flex sm:flex-row gap-2 ">
            <button
              type="button"
              onClick={() => {
                reset({ ...userData }) // Reset form to original data
                setSaved(true) // Exit edit mode
              }}
              className="items-center w-[150px] h-[45px] border rounded-lg shadow-sm text-nr font-[400]   text-SeabiscuitLightTextColor bg-SeabiscuitLightThemeColor hover:text-white hover:bg-SeabiscuitMainThemeColor focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-SeabiscuitMainThemeColor"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
              className="items-center w-[150px] h-[45px] border border-SeabiscuitMainThemeColor rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitMainThemeColor saveBtn"
            >
              {loading ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative ">
      <FormHeader
        title="Membership"
        removeBreakLine
        description="Review and manage your memberships"
        headerButtonsContainer={renderHeaderButtons()}
      />

      {/* Discipline tabs */}
      <div className="flex relative mb-6">
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gray-200"></div>

        {/* mobile menu - dropdown */}
        <div className="md:hidden w-full">
          <div className="relative">
            <button
              className="flex items-center justify-between w-full py-2 px-4 font-medium text-sm focus:outline-none text-SeabiscuitMainThemeColor border-b"
              onClick={() => setIsOpen(!isOpen)}
            >
              <span>{activeTab}</span>
              <IoIosArrowDown
                className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isOpen && (
              <div className="absolute z-10 w-full bg-white border shadow-lg rounded-b-md">
                {Object.keys(membershipGroups).map((discipline) => (
                  <button
                    key={discipline}
                    className={`w-full text-left py-3 px-4 text-sm ${
                      activeTab === discipline
                        ? 'bg-gray-50 text-SeabiscuitMainThemeColor'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      setActiveTab(discipline as NumberMembershipGroupKey)
                      setIsOpen(false)
                    }}
                  >
                    {discipline}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* horizontal tabs */}
        <div className="hidden md:flex ">
          {Object.keys(membershipGroups).map((discipline) => (
            <button
              key={discipline}
              className={`py-2 px-6 font-medium text-nr whitespace-nowrap focus:outline-none relative ${
                activeTab === discipline
                  ? 'text-SeabiscuitMainThemeColor'
                  : 'text-gray-400 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab(discipline as NumberMembershipGroupKey)}
            >
              {discipline}
              {activeTab === discipline && (
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-SeabiscuitMainThemeColor"></div>
              )}
            </button>
          ))}
        </div>

        {/* Column Headers for Pegasus tab */}
        {activeTab === 'Pegasus' && (
          <div className="items-center text-sm font-medium text-gray-400  w-full hidden xl:flex">
            <div className="w-[25%] "></div>
            <div className="text-end text-nowrap w-[18%]">Last payment</div>
            <div className="text-center  w-[22%]">Next payment</div>
            <div className="text-center pl-[20px]  w-[15%]">Manage</div>
            <div className="text-end  pr-[5px]  w-[20%]">Message</div>
          </div>
        )}
      </div>

      {searchValue.trim() ? (
        // render search results only if search value is not empty
        <div className="flex flex-col">
          {searchResults.length > 0 ? (
            <div className="membership-grid-container">
              {searchResults.map((membership) => (
                <CompetitorNumberCard
                  key={membership.fieldKey}
                  icon={membership.icon}
                  label={membership.label}
                  fieldKey={membership.fieldKey}
                  saved={saved}
                  register={register}
                  control={control}
                  userData={userData}
                  setUserData={setUserData}
                  setValue={setValue}
                  update={update}
                  watch={watch}
                  // for custom fields remove the index if edited and removed while searching
                  onRemove={
                    membership.type === 'custom'
                      ? () => remove(parseInt(membership.fieldKey.split('.')[1]))
                      : undefined
                  }
                />
              ))}
              <EmptyCards length={searchResults.length} />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              No memberships found matching your search
            </div>
          )}
        </div>
      ) : //  rendering based on active tab
      activeTab === 'Pegasus' ? (
        <PaymentsItems hideSearchControls={true} />
      ) : activeTab === 'Other' ? (
        // other tab rendering
        <div className="flex flex-col">
          <div className="membership-grid-container">
            {/* Completed custom fields */}
            {completedCustomFields.map((field) => {
              // find the index in the fields array to render correct "other" num
              const index = fields.findIndex((f) => f.id === field.id)
              return (
                <CompetitorNumberCard
                  key={field.id}
                  fieldKey={`userExtraDocument.${index}`}
                  saved={saved}
                  register={register}
                  control={control}
                  userData={userData}
                  setUserData={setUserData}
                  setValue={setValue}
                  update={update}
                  watch={watch}
                  onRemove={() => remove(index)}
                />
              )
            })}
            <EmptyCards length={completedCustomFields.length} />
          </div>

          {/* divider between completed and incomplete custom fields */}
          {completedCustomFields.length > 0 && incompleteCustomFields.length > 0 && (
            <div className="my-6 w-full h-[1px] bg-gray-100"></div>
          )}

          {/* incomplete custom fields */}
          <div className="membership-grid-container">
            {incompleteCustomFields.map((field) => {
              // find index in fields array to render correct "other" num for incomplete
              const index = fields.findIndex((f) => f.id === field.id)
              return (
                <CompetitorNumberCard
                  key={field.id}
                  fieldKey={`userExtraDocument.${index}`}
                  saved={saved}
                  register={register}
                  control={control}
                  userData={userData}
                  setUserData={setUserData}
                  setValue={setValue}
                  update={update}
                  watch={watch}
                  onRemove={() => remove(index)}
                />
              )
            })}
            <EmptyCards length={completedCustomFields.length} />
          </div>

          {/* Add number button */}
          <div className="w-auto inline-block">
            <button
              type="button"
              className="flex border items-center justify-start cursor-pointer hover:opacity-70 w-[120px] h-[40px] self-center mt-4 transition-all"
              onClick={() => {
                if (saved) {
                  setSaved(false)
                }
                append({
                  membershipStatus: '',
                  startDate: null,
                  endDate: null,
                  validTill: null,
                  documentFullName: '',
                  documentNumber: '',
                  image: '',
                })
              }}
            >
              <img src="/assets/cp_icons/Cancel-2.svg" className="w-4 rotate-45" alt="" />
              <p className="ml-2 text-sm text-SeabiscuitMainThemeColor">Add number</p>
            </button>
          </div>
        </div>
      ) : (
        // rendering for all standard membership tabs
        <div className="flex flex-col">
          {/* completed standard memberships */}
          {completedMemberships.length > 0 && (
            <div className="membership-grid-container">
              {completedMemberships.map((membership) => (
                <CompetitorNumberCard
                  key={membership.fieldKey}
                  icon={membership.icon}
                  label={membership.label}
                  fieldKey={membership.fieldKey}
                  saved={saved}
                  register={register}
                  control={control}
                  userData={userData}
                  setUserData={setUserData}
                  setValue={setValue}
                  update={update}
                  watch={watch}
                />
              ))}
              <EmptyCards length={completedMemberships.length} />
            </div>
          )}

          {/* divider between completed and incomplete standard memberships */}
          {completedMemberships.length > 0 && incompleteMemberships.length > 0 && (
            <div className="my-6 w-full bg-gray-100 h-[1px]"></div>
          )}

          {/* incomplete standard memberships */}
          <div className="membership-grid-container">
            {incompleteMemberships.map((membership) => (
              <CompetitorNumberCard
                key={membership.fieldKey}
                icon={membership.icon}
                label={membership.label}
                fieldKey={membership.fieldKey}
                saved={saved}
                register={register}
                control={control}
                userData={userData}
                setUserData={setUserData}
                setValue={setValue}
                update={update}
                watch={watch}
              />
            ))}
            <EmptyCards length={completedMemberships.length} />
          </div>
        </div>
      )}
    </div>
  )
}
